import { isNil, get, partition, isArray } from "lodash"
import moment from "moment"
import { ANALYZE_FACTORS_TAKE, COLORS } from "helpers/analyzeConstants"

export function extractDatesFromFirstSeries(data) {
  const firstEntry = data?.[0]
  const dates = firstEntry?.values?.dates ?? []
  return dates.map(({ date, ...rest }) => ({
    ...rest,
    date: moment(date),
  }))
}

// Batch-trend queries fire one query per committed batch (see hooks/intraday.ts's
// useIntradayBatches), each returning a single dated point rather than one query returning
// the full series — merge and sort across all of them instead of reading only data[0].
export function extractDatesFromEachSeries(data) {
  const allDates = (data ?? []).flatMap(entry => entry?.values?.dates ?? [])
  return allDates
    .map(({ date, ...rest }) => ({
      ...rest,
      date: moment(date),
    }))
    .sort((a, b) => a.date.valueOf() - b.date.valueOf())
}

export function buildModelCategoryOptions(modelMetadata, basicOptions) {
  if (!modelMetadata?.categories || !modelMetadata?.factors) return basicOptions

  const categoryFactorCounts = modelMetadata.factors.reduce((counts, factor) => {
    if (factor.category) counts[factor.category] = (counts[factor.category] ?? 0) + 1
    return counts
  }, {})

  const modelCategories = modelMetadata.categories.reduce((acc, category) => {
    if (categoryFactorCounts[category.id] > 1) acc.push({ id: category.id, primaryText: category.label })
    return acc
  }, [])

  return [...basicOptions, ...modelCategories]
}

/**
 * Filter items to only include those that have actual data points and assign colors
 * @param {Array} items - Array of item objects with id, label, etc.
 * @param {Array} data - Array of data objects containing the actual values
 * @param {Array} [seriesColors] - Optional theme-aware color series (from useThemeChartColors().series)
 * @returns {Array} Filtered items that have corresponding data with assigned colors
 */
export function filterItemsWithData(items, data, seriesColors) {
  if (!isArray(items) || !isArray(data) || data.length === 0) {
    return []
  }

  const filteredItems = items.filter(item => {
    return data.some(dataPoint => {
      return !isNil(get(dataPoint, item.id))
    })
  })

  const itemsWithMaxValues = appendMaxAbsValues(filteredItems, data)

  // Separate items that should keep original order (overall items and category totals) from other items (sort by max value)
  const [preserveOrderItems, sortableItems] = partition(
    itemsWithMaxValues,
    ({ item }) => item.type === "overall" || item.id.endsWith("_total"),
  )

  sortableItems.sort((a, b) => b.maxAbsValue - a.maxAbsValue)

  const sortedItems = [...preserveOrderItems.map(({ item }) => item), ...sortableItems.map(({ item }) => item)]

  const limitedItems = sortedItems.slice(0, ANALYZE_FACTORS_TAKE)
  const colors = Array.isArray(seriesColors) && seriesColors.length ? seriesColors : COLORS

  return limitedItems.map((item, index) => ({
    ...item,
    color: item.color || colors[index % colors.length],
  }))
}

export function appendMaxAbsValues(items, data) {
  if (!isArray(items) || !isArray(data) || data.length === 0) {
    return []
  }

  return items.map(item => {
    const maxAbsValue = data.reduce((max, dataPoint) => {
      const value = get(dataPoint, item.id)
      const absValue = isNil(value) ? 0 : Math.abs(value)
      return Math.max(max, absValue)
    }, 0)
    return { item, maxAbsValue }
  })
}
